import type { AudioInfo } from '@/sanskrit/lib/audio'

import { useEffect, useRef, useState } from 'react'
import { Volume2, Snail, Loader2, VolumeX } from 'lucide-react'
import { cn } from '@/sanskrit/lib/utils'
import { useAppState, useT } from '@/sanskrit/components/providers/app-state'
import { usePrefs } from '@/lib/client/prefs'
import { useSession } from '@/lib/client/session'

import {
    audioInfo,
    devanagariVoices,
    onVoicesChanged,
    speak,
    speechSupported,
    stopSpeaking
} from '@/sanskrit/lib/audio'

/** Re-renders when the device's voice list becomes available (Chrome loads it lazily). */
export const useVoices = () => {
    const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
    const [ready, setReady] = useState(false)

    useEffect(() => {
        if (!speechSupported()) {
            setReady(true)
            return
        }

        const load = () => {
            setVoices(devanagariVoices())
            setReady(true)
        }

        load()
        const stop = onVoicesChanged(load)
        const timer = window.setTimeout(load, 1200)

        return () => {
            stop()
            window.clearTimeout(timer)
        }
    }, [])

    return { voices, ready }
}

export const useAudioInfo = (text?: string): AudioInfo | null => {
    const { settings } = useAppState()
    const { ready, voices } = useVoices()
    const { voice: engine } = usePrefs()
    const { access } = useSession()
    const [info, setInfo] = useState<AudioInfo | null>(null)
    const premium = access.prakrit || access.sanskrit

    // The voice choice (Bhashini or the device) comes from the shared preferences.
    useEffect(() => {
        if (!ready) return
        setInfo(audioInfo(text, settings.voiceURI))
    }, [ready, voices, text, settings.voiceURI, engine, premium])

    return info
}

interface AudioButtonProps {
    text: string
    slow?: boolean
    size?: 'sm' | 'md' | 'lg'
    variant?: 'icon' | 'pill' | 'board'
    label?: string
    className?: string
    onPlayed?: () => void
}

export default function AudioButton({ text, slow = false, size = 'md', variant = 'icon', label, className, onPlayed }: AudioButtonProps) {
    const t = useT()
    const { settings } = useAppState()
    const info = useAudioInfo(text)
    const [state, setState] = useState<'idle' | 'playing' | 'unavailable'>('idle')
    const mounted = useRef(true)

    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
        }
    }, [])

    const unavailable = info?.kind === 'unavailable'
    const rate = slow ? Math.max(0.45, settings.rate * 0.65) : settings.rate

    const play = async () => {
        if (state === 'playing') {
            stopSpeaking()
            setState('idle')
            return
        }

        setState('playing')
        const kind = await speak(text, { rate, voiceURI: settings.voiceURI })
        if (!mounted.current) return
        setState(kind === 'unavailable' ? 'unavailable' : 'idle')
        if (kind !== 'unavailable') onPlayed?.()
    }

    const sizes = { sm: 'size-8', md: 'size-10', lg: 'size-12' }
    const iconSizes = { sm: 'size-4', md: 'size-5', lg: 'size-6' }
    const title = unavailable || state === 'unavailable'
        ? t({ hi: 'इस डिवाइस पर उच्चारण-आवाज़ उपलब्ध नहीं है', en: 'No pronunciation voice is available on this device' })
        : info?.kind === 'device-voice' && info.approximate
            ? t({ hi: `डिवाइस की आवाज़ (${info.voiceLang}): अनुमानित उच्चारण`, en: `Device voice (${info.voiceLang}): approximate pronunciation` })
            : slow
                ? t({ hi: 'धीरे सुनें', en: 'Listen slowly' })
                : t({ hi: 'सुनें', en: 'Listen' })

    const Icon = state === 'playing' ? Loader2 : unavailable || state === 'unavailable' ? VolumeX : slow ? Snail : Volume2

    if (variant === 'pill' || variant === 'board') {
        return (
            <button
                type='button'
                onClick={play}
                title={title}
                aria-label={label ?? title}
                className={cn(
                    'inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-medium transition-colors',
                    variant === 'pill' && 'border border-border bg-card text-foreground hover:bg-saffron-soft',
                    variant === 'board' && 'border border-white/15 bg-white/10 text-chalk hover:bg-white/20',
                    (unavailable || state === 'unavailable') && 'opacity-70',
                    className
                )}
            >
                <Icon className={cn('size-4', state === 'playing' && 'animate-spin')} />
                <span>{label ?? (slow ? t({ hi: 'धीरे सुनें', en: 'Slowly' }) : t({ hi: 'सुनें', en: 'Listen' }))}</span>
            </button>
        )
    }

    return (
        <button
            type='button'
            onClick={play}
            title={title}
            aria-label={label ?? `${title}: ${text}`}
            className={cn(
                'inline-flex shrink-0 items-center justify-center rounded-full border border-border bg-card text-ink shadow-sm transition-colors hover:bg-saffron-soft',
                sizes[size],
                (unavailable || state === 'unavailable') && 'opacity-60',
                className
            )}
        >
            <Icon className={cn(iconSizes[size], state === 'playing' && 'animate-spin')} />
        </button>
    )
}
